import { t as uiText, enText, jaText, matchesUiText } from '../i18n';
/** URL / CSV helpers for the setup-agent ingest flow (client-side). */

import { detectCsvDelimiter, parseCsvRecords, stripBom, mergeUrlContinuationRows } from './csvParse';

const URL_RE = /https?:\/\/[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+/g;

function stripTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?)\]}】」』>。、！？）＞]+$/u, '');
}

export function normalizeUrl(input: string): string | null {
  const raw = stripTrailingPunctuation((input || '').trim());
  if (!raw) return null;
  // JS accepts `https://日本語` as IDN (punycode). That is not a real crawl URL.
  let parsed: URL;
  try {
    if (/^https?:\/\//i.test(raw)) {
      parsed = new URL(raw);
    } else if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}([/:?#].*)?$/i.test(raw)) {
      parsed = new URL(`https://${raw}`);
    } else {
      return null;
    }
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  const host = parsed.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return parsed.toString();
  if (!host.includes('.')) return null;
  return parsed.toString();
}

export function extractUrlsFromText(text: string): string[] {
  if (!text) return [];
  const found = text.match(URL_RE) || [];
  const normalized = found
    .map((u) => normalizeUrl(u))
    .filter((u): u is string => !!u);
  return Array.from(new Set(normalized));
}

function isPagesHeaderRow(cells: string[]): boolean {
  const lower = cells.map((h) => h.trim().toLowerCase());
  return (
    lower.some((h) => [uiText("Title").toLowerCase(), jaText("Title").toLowerCase(), 'title'].includes(h)) ||
    lower.some((h) => [uiText("Content").toLowerCase(), jaText("Content").toLowerCase(), uiText("Contents").toLowerCase(), jaText("Contents").toLowerCase(), 'content', 'body'].includes(h))
  );
}

export interface ParsedCrawlCsv {
  /** 重複除去済みの有効 URL 一覧 */
  urls: string[];
  /** ヘッダーを除いた総行数（プレースホルダー・空行含む） */
  rawCount: number;
  /** URL として無効だった行数（空行・スキームなし等） */
  invalidCount: number;
  /** 入力内の重複行数（正規化後に同一になるもの） */
  duplicateCount: number;
  /** `{...}` を含むプレースホルダー行数 */
  placeholderCount: number;
}

/** プレースホルダーパターン（例: {記事ID}） */
const PLACEHOLDER_RE = /\{[^}]*\}/;

/** True when every non-empty row under the header starts with a URL. */
function firstColumnIsUrlList(records: string[][]): boolean {
  let rows = 0;
  for (let i = 1; i < records.length; i++) {
    const cell = (records[i][0] || '').trim();
    if (!cell) continue;
    if (!normalizeUrl(cell)) return false;
    rows++;
  }
  return rows > 0;
}

/** Parse crawl-target CSV. First row may be a header ("URL") or an actual URL. */
export function parseCrawlCsv(text: string): string[] {
  return parseCrawlCsvDetailed(text).urls;
}

/** Parse crawl-target CSV and return detailed skip counts. */
export function parseCrawlCsvDetailed(text: string): ParsedCrawlCsv {
  const delimiter = detectCsvDelimiter(text);
  const records = parseCsvRecords(text, delimiter);

  const empty: ParsedCrawlCsv = { urls: [], rawCount: 0, invalidCount: 0, duplicateCount: 0, placeholderCount: 0 };
  if (records.length === 0) return empty;
  // A URL list exported with a Title/Content header is still a URL list. Reading
  // it as pages would store the URLs themselves as page titles and bodies.
  if (isPagesHeaderRow(records[0]) && !firstColumnIsUrlList(records)) return empty;

  const firstCell = (records[0][0] || '').trim();
  const start = /^https?:\/\//i.test(firstCell) ? 0 : 1;

  const rawCount = records.length - start;
  let invalidCount = 0;
  let placeholderCount = 0;
  let duplicateCount = 0;

  const seen = new Set<string>();
  const urls: string[] = [];

  for (let i = start; i < records.length; i++) {
    const cell = records[i][0] || '';

    // プレースホルダー検出（{記事ID} 等）
    if (PLACEHOLDER_RE.test(cell)) {
      placeholderCount++;
      continue;
    }

    const normalized = normalizeUrl(cell);
    if (!normalized) {
      invalidCount++;
      continue;
    }

    if (seen.has(normalized)) {
      duplicateCount++;
      continue;
    }

    seen.add(normalized);
    urls.push(normalized);
  }

  // CSV に有効 URL が1件もなければ本文テキストから抽出（フォールバック）
  if (urls.length === 0) {
    const fallback = extractUrlsFromText(text);
    return { urls: fallback, rawCount, invalidCount: rawCount, duplicateCount: 0, placeholderCount: 0 };
  }

  return { urls, rawCount, invalidCount, duplicateCount, placeholderCount };
}

export interface ParsedPagesCsv {
  csvText: string;
  delimiter: string;
  hasHeader: boolean;
  titleIndex: number;
  contentIndex: number;
  titles: string[];
  /** Title + content start, for chat preview */
  previews: Array<{ title: string; snippet: string }>;
  rowCount: number;
}

export function parsePagesCsv(text: string): ParsedPagesCsv {
  const csvText = stripBom(text);
  const delimiter = detectCsvDelimiter(csvText);
  const records = parseCsvRecords(csvText, delimiter);
  const empty: ParsedPagesCsv = {
    csvText,
    delimiter,
    hasHeader: true,
    titleIndex: 0,
    contentIndex: 1,
    titles: [],
    previews: [],
    rowCount: 0,
  };
  if (records.length === 0) return empty;

  const headerCells = records[0];
  const lower = headerCells.map((h) => h.trim().toLowerCase());
  const hasHeader = isPagesHeaderRow(headerCells);
  const titleIndex = hasHeader
    ? Math.max(0, lower.findIndex((h) => [uiText("Title"), 'title'].includes(h)))
    : 0;
  const guessedContent = hasHeader
    ? lower.findIndex((h) => [uiText("Content"), uiText("Contents"), 'content', 'body'].includes(h))
    : 1;
  const contentIndex = guessedContent >= 0 ? guessedContent : Math.min(1, headerCells.length - 1);
  const dataStart = hasHeader ? 1 : 0;
  const table = mergeUrlContinuationRows(records, titleIndex, contentIndex, dataStart);
  const titles: string[] = [];
  const previews: Array<{ title: string; snippet: string }> = [];
  for (let i = dataStart; i < table.length; i++) {
    const cols = table[i];
    const t = (cols[titleIndex] || '').trim();
    const rawContent = cols[contentIndex] || '';
    if (!t && !rawContent.trim()) continue;
    const title = t || uiText("(No title)");
    titles.push(title);
    previews.push({ title, snippet: rawContent.replace(/\s+/g, ' ').trim().slice(0, 80) });
  }
  return {
    csvText,
    delimiter,
    hasHeader,
    titleIndex,
    contentIndex,
    titles,
    previews,
    rowCount: titles.length,
  };
}

export function downloadCsvTemplate(kind: 'crawl' | 'pages'): void {
  const csvContent = kind === 'crawl' ? '\uFEFFURL\n' : uiText("﻿Title,Content\n");
  const filename = kind === 'crawl' ? 'notreu-crawl-urls-template.csv' : 'notreu-import-template.csv';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export type IngestMode = 'none' | 'crawl-csv' | 'pages-csv' | 'crawl-text' | 'page-text';

export type IngestPath = 'crawl' | 'pages' | null;
export type IngestInput = 'csv' | 'text' | null;
export type IngestStep =
  | 'idle'
  | 'choose-path'
  | 'choose-input'
  | 'awaiting-upload'
  | 'preview'
  | 'done';

export type IngestFlowState = {
  path: IngestPath;
  input: IngestInput;
  step: IngestStep;
};

export const IDLE_INGEST_FLOW: IngestFlowState = {
  path: null,
  input: null,
  step: 'idle',
};

export const CONTINUE_SAME_WORK = jaText("Continue this task");
export const START_NEW_WORK = jaText("Switch task");
export const AFTER_SUCCESS_CHOICES = [CONTINUE_SAME_WORK, START_NEW_WORK];

export const CRAWL_NOW_CHOICE = jaText("Start crawl now");
export const CRAWL_LATER_CHOICE = jaText("Start later");
export const SEED_ONLY_CHOICE = jaText("Include only this URL");
export const DISCOVER_CHILDREN_CHOICE = jaText("Also find child pages");
export const MORE_URLS_CHOICE = jaText("I have more URLs to register");
export const CONFIRM_REGISTER_CHOICE = jaText("Register this content");
export const REDO_CHOICE = jaText("Start over");
export const AFTER_REGISTER_SCOPE_CHOICES = [SEED_ONLY_CHOICE, DISCOVER_CHILDREN_CHOICE];
export const PREVIEW_SCOPE_CHOICES = [SEED_ONLY_CHOICE, DISCOVER_CHILDREN_CHOICE, REDO_CHOICE];
export const AFTER_SCOPE_CRAWL_CHOICES = [CRAWL_NOW_CHOICE, MORE_URLS_CHOICE];
/** @deprecated Use AFTER_SCOPE_CRAWL_CHOICES. Kept for old transcripts. */
export const CRAWL_CONTINUATION_CHOICES = AFTER_SCOPE_CRAWL_CHOICES;
/** After crawl job completes: same-intent next is more URLs, not ingestFlow continue/switch. */
export const AFTER_CRAWL_COMPLETE_CHOICES = [MORE_URLS_CHOICE];
export const START_CRAWL_OPERATION_ID = 'ingestFromWebsite.startCrawl';
export const AGENT_REGISTER_URL_LIMIT = 200;
export const CRAWL_PREVIEW_SCOPE_QUESTION =
  uiText("Crawl only this URL, or also include subpages on the same site?");
const CRAWL_PREVIEW_SCOPE_QUESTION_KEY =
  "Crawl only this URL, or also include subpages on the same site?";

export type ChatActionLike = {
  type: string;
  label: string;
  params?: Record<string, unknown>;
  alreadyExecuted?: boolean;
  output?: Record<string, unknown>;
};

export type ChatContinuation = {
  operationId: string;
  label?: string;
};

/** Keep Loop alreadyExecuted on confirm; only fill urls from the preview list. */
export function resolveConfirmedCrawlAction(args: {
  action?: ChatActionLike;
  lastPreviewUrls: string[];
  workspaceId: string;
}): ChatActionLike {
  const previewUrls = args.lastPreviewUrls;
  const existingUrls = Array.isArray(args.action?.params?.urls)
    ? (args.action!.params!.urls as unknown[]).filter((u): u is string => typeof u === 'string')
    : [];
  const urls = previewUrls.length > 0 ? previewUrls : existingUrls;

  if (args.action?.alreadyExecuted === true && args.action.type === 'registerCrawlTarget') {
    return {
      ...args.action,
      params: { ...args.action.params, workspaceId: args.workspaceId, urls },
    };
  }

  return {
    type: 'registerCrawlTarget',
    label: args.action?.label || uiText("Register URL"),
    params: { workspaceId: args.workspaceId, urls },
  };
}

/** After URL register: scope was already asked at preview. Next is crawl vs more URLs. */
export function choicesAfterSyncSuccess(args: {
  continuation?: ChatContinuation;
  existingChoices?: string[];
  actionType?: string;
}): string[] {
  if (args.actionType === 'registerCrawlTarget') {
    return [...AFTER_SCOPE_CRAWL_CHOICES];
  }
  if (args.continuation?.operationId === START_CRAWL_OPERATION_ID) {
    return [...AFTER_SCOPE_CRAWL_CHOICES];
  }
  return [...AFTER_SUCCESS_CHOICES];
}

/** Chips after an async job finishes. Crawl complete is Continuation, not ingestFlow. */
export function choicesAfterAsyncJob(args: {
  success: boolean;
  trackingType?: 'crawlerExecution' | 'suggestRagRun' | string;
}): string[] | undefined {
  if (!args.success) return undefined;
  if (args.trackingType === 'crawlerExecution') return [...AFTER_CRAWL_COMPLETE_CHOICES];
  return [...AFTER_SUCCESS_CHOICES];
}

export function shouldAskCrawlScope(urlCount: number): boolean {
  return urlCount === 1;
}

export function choicesForCrawlPreview(urlCount: number): string[] {
  if (shouldAskCrawlScope(urlCount)) {
    return [...PREVIEW_SCOPE_CHOICES];
  }
  return [CONFIRM_REGISTER_CHOICE, REDO_CHOICE];
}

export function rewriteCrawlPreviewReply(reply: string, urlCount: number): string {
  if (!shouldAskCrawlScope(urlCount)) return reply;
  const trimmed = reply.trim();
  const question = uiText(CRAWL_PREVIEW_SCOPE_QUESTION_KEY);
  if (trimmed.includes(question) || trimmed.includes(jaText(CRAWL_PREVIEW_SCOPE_QUESTION_KEY))) return trimmed;
  return `${trimmed}\n\n${question}`;
}

export function isCrawlRegisterConfirm(label: string): boolean {
  const t = label.trim();
  return t === CONFIRM_REGISTER_CHOICE || t === SEED_ONLY_CHOICE;
}

export function continueSameWorkGuidance(flow: IngestFlowState): string {
  if (flow.path === 'pages' && flow.input === 'text') {
    return uiText("You can create the next item the same way. Enter the body in the card below. The title can be blank.");
  }
  if (flow.path === 'pages' && flow.input === 'csv') {
    return uiText("Continuing the same way. Drop a CSV, or choose one with 📎.");
  }
  if (flow.path === 'crawl' && flow.input === 'text') {
    return uiText("Continuing the same way. Paste a URL.");
  }
  if (flow.path === 'crawl' && flow.input === 'csv') {
    return uiText("Continuing the same way. Drop a CSV, or choose one with 📎.");
  }
  return uiText("You can continue the same kind of task. Type what you want, or pick a suggestion below.");
}

export type IngestTransitionEvent =
  | { type: 'choice'; label: string }
  | { type: 'offerCsvTemplate'; kind: 'crawl' | 'pages' }
  | { type: 'csvPreview' }
  | { type: 'confirmed' }
  | { type: 'redo' }
  | { type: 'reset' };

const INGEST_STEPS: IngestStep[] = [
  'idle',
  'choose-path',
  'choose-input',
  'awaiting-upload',
  'preview',
  'done',
];

/** Message shape used to recover flow from older sessions. */
export type IngestInferMessage = {
  content?: string;
  choices?: string[];
  pagePreviewList?: string[];
  crawlTargetList?: { url: string; status: string }[];
};

/** Max URLs sent to agentChatFn. Larger lists make gpt-4o-mini truncate JSON (maxTokens 800). */
export const AGENT_CHAT_LLM_URL_LIMIT = 8;

/** How many URLs to render inline in chat. The full list stays on the message. */
export const CRAWL_PREVIEW_DISPLAY_LIMIT = 30;

export function urlsForAgentChatLlm(urls: string[]): string[] | undefined {
  if (urls.length === 0) return undefined;
  if (urls.length > AGENT_CHAT_LLM_URL_LIMIT) return undefined;
  return urls;
}

export function markCrawlTargetsRegistered<
  T extends { crawlTargetList?: { url: string; status: string }[] },
>(messages: T[], registeredUrls: string[]): T[] {
  const registered = new Set(
    registeredUrls
      .map((u) => normalizeUrl(u) ?? u.trim())
      .filter((u) => u.length > 0)
  );
  if (registered.size === 0) return messages;

  let changed = false;
  const next = messages.map((m) => {
    const list = m.crawlTargetList;
    if (!list || list.length === 0) return m;
    let listChanged = false;
    const nextList = list.map((t) => {
      const key = normalizeUrl(t.url) ?? t.url;
      if (!registered.has(key) || t.status === 'exists') return t;
      listChanged = true;
      return { ...t, status: 'exists' };
    });
    if (!listChanged) return m;
    changed = true;
    return { ...m, crawlTargetList: nextList };
  });
  return changed ? next : messages;
}

export function recoverPreviewUrlsFromMessages(
  messages: Array<{ crawlTargetList?: { url: string; status: string }[] }>
): string[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const list = messages[i].crawlTargetList;
    if (!list || list.length === 0) continue;
    if (list.some((t) => t.status === 'preview' || t.status === 'new' || t.status === 'exists')) {
      return list.map((t) => t.url).filter((u) => typeof u === 'string' && u.length > 0);
    }
  }
  return [];
}

export function keepLongerUrlList(current: string[], incoming: string[]): string[] {
  if (incoming.length > current.length) return incoming;
  if (current.length === 0) return incoming;
  return current;
}

export function ingestModeFromFlow(flow: IngestFlowState): IngestMode {
  if (flow.path === 'pages' && flow.input === 'csv') return 'pages-csv';
  if (flow.path === 'pages' && flow.input === 'text') return 'page-text';
  if (flow.path === 'crawl' && flow.input === 'csv') return 'crawl-csv';
  if (flow.path === 'crawl' && flow.input === 'text') return 'crawl-text';
  return 'none';
}

export function normalizeIngestFlow(raw: unknown): IngestFlowState | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const path: IngestPath =
    o.path === 'crawl' || o.path === 'pages' || o.path === null
      ? (o.path as IngestPath)
      : null;
  const input: IngestInput =
    o.input === 'csv' || o.input === 'text' || o.input === null
      ? (o.input as IngestInput)
      : null;
  const step: IngestStep = INGEST_STEPS.includes(o.step as IngestStep)
    ? (o.step as IngestStep)
    : 'idle';
  return { path, input, step };
}

/** Choice labels from before the custom-answer rename. */
const LEGACY_CREATE_CUSTOM_ANSWER_LABELS = new Set([
  'notreuにページを入れる',
  'Add pages to notreu',
]);

/** The wizard questions the model is told to answer with verbatim labels. */
const INGEST_CHOICE_KEYS = [
  'Crawl an existing site',
  'Add pages to notreu',
  'Upload a CSV',
  'Paste as text',
  'Import a CSV',
  'Add one item as text',
];

const CHOICE_STOPWORDS = new Set(['a', 'an', 'the', 'as', 'for', 'to', 'of', 'this', 'your', 'my']);

function choiceTokens(label: string): Set<string> {
  return new Set(
    label
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .split(' ')
      .filter((w) => w && !CHOICE_STOPWORDS.has(w))
  );
}

/**
 * The model authors the chip labels and is told to echo these verbatim, but it
 * rewrites them whenever it replies in another language ("Crawl existing site",
 * "CSV upload"). An unmatched label leaves `ingestFlow.path` unset, which drops
 * the admin into the crawl branch no matter which chip they pressed, so match on
 * the token set rather than on the exact string.
 */
function resolveIngestChoiceKey(label: string): string | null {
  const trimmed = label.trim();
  if (!trimmed) return null;
  for (const key of INGEST_CHOICE_KEYS) {
    if (matchesUiText(trimmed, key)) return key;
  }
  const tokens = choiceTokens(trimmed);
  if (tokens.size < 2) return null;
  let bestKey: string | null = null;
  let bestScore = 0;
  let secondScore = 0;
  for (const key of INGEST_CHOICE_KEYS) {
    const target = choiceTokens(enText(key));
    if (target.size === 0) continue;
    const shared = [...tokens].filter((w) => target.has(w)).length;
    const score = shared / Math.max(tokens.size, target.size);
    if (score > bestScore) {
      secondScore = bestScore;
      bestScore = score;
      bestKey = key;
    } else if (score > secondScore) {
      secondScore = score;
    }
  }
  return bestScore >= 0.6 && bestScore > secondScore ? bestKey : null;
}

export function canonicalIngestChoice(label: string): string {
  const t = label.trim();
  if (LEGACY_CREATE_CUSTOM_ANSWER_LABELS.has(t)) return jaText('Add pages to notreu');
  const key = resolveIngestChoiceKey(t);
  return key ? jaText(key) : t;
}

export const INGEST_PATH_CHOICES = [jaText('Crawl an existing site'), jaText('Add pages to notreu')];
export const CRAWL_INPUT_CHOICES = [jaText('Upload a CSV'), jaText('Paste as text')];
export const PAGES_INPUT_CHOICES = [jaText('Import a CSV'), jaText('Add one item as text')];

/**
 * Both wizard questions are fully determined by the flow, so the model must not
 * author their chips. Token matching alone is not enough: a paraphrase like
 * "Text entry" shares no words with "Add one item as text" and would leave the
 * chip dead. Returns null when this turn is not a wizard question.
 */
export function canonicalIngestChoiceSet(
  flow: IngestFlowState,
  choices: string[] | undefined
): string[] | null {
  if (!choices || choices.length < 2) return null;
  if (flow.step === 'choose-input') {
    if (flow.path === 'pages') return PAGES_INPUT_CHOICES;
    if (flow.path === 'crawl') return CRAWL_INPUT_CHOICES;
    return null;
  }
  if (flow.path) return null;
  const canonical = choices.map(canonicalIngestChoice);
  return canonical.some((c) => INGEST_PATH_CHOICES.includes(c)) ? INGEST_PATH_CHOICES : null;
}

export function ingestChoiceLabel(label: string): string {
  return uiText(canonicalIngestChoice(label));
}

/** Show the renamed custom-answer wording, including inside older chat replies. */
export function displayIngestWording(content: string): string {
  const next = uiText('Add pages to notreu');
  return content
    .replaceAll('notreuにページを入れる', next)
    .replaceAll('Add pages to notreu', next);
}

function applyChoice(flow: IngestFlowState, label: string): IngestFlowState {
  const t = canonicalIngestChoice(label);
  if (t === jaText("Crawl an existing site")) {
    return { path: 'crawl', input: null, step: 'choose-input' };
  }
  if (t === jaText("Add pages to notreu")) {
    return { path: 'pages', input: null, step: 'choose-input' };
  }
  if (t === jaText("Upload a CSV")) {
    return { path: flow.path ?? 'crawl', input: 'csv', step: 'awaiting-upload' };
  }
  if (t === jaText("Import a CSV")) {
    return { path: flow.path ?? 'pages', input: 'csv', step: 'awaiting-upload' };
  }
  if (t === jaText("Paste as text")) {
    return { path: flow.path ?? 'crawl', input: 'text', step: 'awaiting-upload' };
  }
  if (t === jaText("Add one item as text")) {
    return { path: flow.path ?? 'pages', input: 'text', step: 'awaiting-upload' };
  }
  if (t === MORE_URLS_CHOICE) {
    return { path: 'crawl', input: flow.input ?? 'text', step: 'awaiting-upload' };
  }
  if (t === CONTINUE_SAME_WORK) {
    if (!flow.path) return flow;
    return { ...flow, step: 'awaiting-upload' };
  }
  if (t === START_NEW_WORK) {
    return { ...IDLE_INGEST_FLOW };
  }
  if (t === jaText("Start over")) {
    if (!flow.path) return flow;
    return { ...flow, step: 'awaiting-upload' };
  }
  return flow;
}

export function applyIngestTransition(
  flow: IngestFlowState,
  event: IngestTransitionEvent
): IngestFlowState {
  switch (event.type) {
    case 'choice':
      return applyChoice(flow, event.label);
    case 'offerCsvTemplate': {
      const kind = flow.path ?? event.kind;
      return {
        path: kind === 'pages' ? 'pages' : 'crawl',
        input: 'csv',
        step: 'awaiting-upload',
      };
    }
    case 'csvPreview':
      return { ...flow, input: flow.input ?? 'csv', step: 'preview' };
    case 'confirmed':
      return { ...flow, step: 'done' };
    case 'redo':
      if (!flow.path) return flow;
      return { ...flow, step: 'awaiting-upload' };
    case 'reset':
      return { ...IDLE_INGEST_FLOW };
    default:
      return flow;
  }
}

export function coerceFlowForRestore(
  flow: IngestFlowState,
  hasPendingPayload: boolean
): IngestFlowState {
  if (hasPendingPayload) return flow;
  if ((flow.step === 'preview' || flow.step === 'done') && flow.path) {
    return { ...flow, step: 'awaiting-upload' };
  }
  return flow;
}

const PAGE_TEXT_NOUNS = [/ページ/, /カスタム回答/, /\bpages?\b/i, /\bcustom answer/i];
const PAGE_TEXT_HINTS = [
  /テキスト/,
  /手入力/,
  /直接入力/,
  /打ち込/,
  /\btext\b/i,
  /\btype\b/i,
  /\bwrite\b/i,
  /\bmanual/i,
];

/**
 * The admin wants to add one page by typing it. The create card is driven by the
 * flow, so a request the flow does not recognise leaves the model describing a
 * card that is not on screen. Both a page noun and a typing hint are required, so
 * "also look for pages under this site" does not match.
 */
export function isPageTextRequest(text: string): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  if (/csv/i.test(t) || /https?:\/\//i.test(t)) return false;
  return PAGE_TEXT_NOUNS.some((p) => p.test(t)) && PAGE_TEXT_HINTS.some((p) => p.test(t));
}

/** Message shape the restore coercion needs; `ChatMessage` satisfies it. */
export type RestorableChoiceMessage = {
  id: string;
  role: string;
  choices?: string[];
  choicesUsed?: boolean;
};

/**
 * A restored conversation has no parsed CSV in memory and no turn in flight, so
 * only its final assistant message may still offer chips - and not the ones that
 * need that CSV, which would answer "importing now" and then do nothing.
 */
export function coerceChoicesForRestore<T extends RestorableChoiceMessage>(
  messages: T[],
  hasPendingPayload: boolean
): T[] {
  const lastAssistantId = [...messages].reverse().find((m) => m.role === 'assistant')?.id;
  return messages.map((m) => {
    if (!m.choices || m.choices.length === 0 || m.choicesUsed) return m;
    if (m.id !== lastAssistantId) return { ...m, choicesUsed: true };
    if (!hasPendingPayload && m.choices.some((c) => matchesUiText(c, 'Import this content'))) {
      return { ...m, choicesUsed: true };
    }
    return m;
  });
}

function sameChoiceSet(actual: string[] | undefined, expected: readonly string[]): boolean {
  if (!actual || actual.length !== expected.length) return false;
  return actual.every((item, index) => item === expected[index]);
}

/** Register / crawl-complete chips mean the preview path is finished. */
export function leavesPreviewAfterCrawlRegister(choices: string[] | undefined): boolean {
  return (
    sameChoiceSet(choices, AFTER_SCOPE_CRAWL_CHOICES) ||
    sameChoiceSet(choices, AFTER_CRAWL_COMPLETE_CHOICES)
  );
}

export function inferIngestFlowFromMessages(
  messages: IngestInferMessage[]
): IngestFlowState {
  let flow: IngestFlowState = { ...IDLE_INGEST_FLOW };
  for (const m of messages) {
    if (m.pagePreviewList && m.pagePreviewList.length > 0) {
      flow = { path: 'pages', input: flow.input ?? 'csv', step: 'preview' };
    } else if (m.crawlTargetList && m.crawlTargetList.length > 0) {
      flow = { path: 'crawl', input: flow.input ?? 'csv', step: 'preview' };
    }
    const label = (m.content || '').trim();
    if (label) {
      flow = applyChoice(flow, label);
    }
    if (leavesPreviewAfterCrawlRegister(m.choices)) {
      flow = { ...IDLE_INGEST_FLOW };
    }
  }
  return coerceFlowForRestore(flow, false);
}
